import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { FinancialTransactionWithTerreiroId } from "../repositories/incoming.repository";
import { outgoingRepository } from "../repositories/outgoing.repository";
import { financialQueryKeys } from "./financialQueryKeys";
import { invalidateAccountBalanceQueries } from "./invalidateAccountBalanceQueries";
import { invalidateInventoryStorageQueries } from "./useTerreiroInventoryListQuery";
import { invalidateSupplyPlanQueries } from "./useSupplyPlanQuery";

/**
 * Mutation hook to create an outgoing transaction (saida).
 * Uses repository for network call and performs targeted invalidation on settle.
 * Supports both terreiro and medium contexts.
 */
export function useCreateOutgoingMutation(
  terreiroId?: string,
  mediumId?: string
) {
  const qc = useQueryClient();
  const mutationKey = ["outgoing", terreiroId, mediumId, "create"];

  return useMutation<unknown, Error, FinancialTransactionWithTerreiroId>({
    mutationKey,
    mutationFn: (payload) => outgoingRepository.createOutgoing(payload),
    retry: 0,
    onSettled: () => {
      // Refresh the movement list and the account balances shown on Contas.
      const promises: Promise<unknown>[] = [
        qc.invalidateQueries({
          queryKey: financialQueryKeys.currentMonthOutcome(
            terreiroId,
            mediumId
          ),
        }),
        invalidateAccountBalanceQueries(qc, { terreiroId, mediumId }),
      ];
      if (terreiroId) {
        promises.push(
          qc.invalidateQueries({
            queryKey: financialQueryKeys.monthlyFees(terreiroId),
          })
        );
      }
      invalidateSupplyPlanQueries(qc, terreiroId, mediumId);
      promises.push(
        invalidateInventoryStorageQueries(qc, terreiroId, mediumId),
      );
      return Promise.all(promises);
    },
  });
}
